import { memo, useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box } from '@mui/material';
import { FormContainer } from 'components/layout';
import { AdAccountSelect } from 'components/common';
import type { Option } from 'components/common';
import datadogActions from 'lib/datadog';
import { useLocationParams } from 'hooks';
import { useTwitterAccounts } from '../api';
import { clearTwitterDownstreamSelections } from '../utils/formSelections';

const ChooseAccount = memo(() => {
  const { setValue } = useFormContext();
  const app = (useWatch({ name: 'app' }) as string | undefined) ?? '';
  const accountId = (useWatch({ name: 'accountId' }) as string | undefined) ?? '';
  const {
    setAdAccount: setAdAccountParam,
    setSelectedCampaigns: setSelectedCampaignsParam,
    setSelectedAdSets: setSelectedAdSetsParam,
    setFilterCampaignsByName: setFilterCampaignsByNameParam,
    setFilterAdSetsByName: setFilterAdSetsByNameParam,
    setAdSettingsFlow: setAdSettingsFlowParam,
  } = useLocationParams();

  const { data, isLoading, isError, error } = useTwitterAccounts<Option[]>({
    params: { app },
    config: {
      enabled: !!app,
      select: (accounts) =>
        accounts.map((account) => ({ value: account.ad_account_id, label: account.name })),
    },
  });

  const handleChangeAccount = useCallback(() => {
    clearTwitterDownstreamSelections(setValue);
    setSelectedCampaignsParam([]);
    setSelectedAdSetsParam([]);
    setFilterCampaignsByNameParam([]);
    setFilterAdSetsByNameParam([]);
    setAdSettingsFlowParam({});
  }, [
    setValue,
    setAdSettingsFlowParam,
    setFilterAdSetsByNameParam,
    setFilterCampaignsByNameParam,
    setSelectedAdSetsParam,
    setSelectedCampaignsParam,
  ]);

  useEffect(() => {
    setAdAccountParam(accountId || undefined);
  }, [accountId, setAdAccountParam]);

  useEffect(() => {
    if (!isError || !error) return;

    datadogActions.trackDatadogError(error as Error, {
      source: 'twitter_ad_account_select_query',
      app,
    });
  }, [isError, error, app]);

  return (
    <FormContainer title="Choose Ad Account">
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
        <Box sx={{ display: 'flex', flex: 1 }}>
          <AdAccountSelect
            name="accountId"
            inputLabel="Ad Account"
            options={data}
            isLoading={isLoading}
            onChange={handleChangeAccount}
          />
        </Box>
        <Box sx={{ display: 'flex', flex: 1 }} />
      </Box>
    </FormContainer>
  );
});

ChooseAccount.displayName = 'ChooseAccount';

export default ChooseAccount;
